<script setup lang="ts">
// ── Top-level crash screen ───────────────────────────────────────────────────
// Vue 3 has no <ErrorBoundary> component, so this is the hand-rolled equivalent:
// onErrorCaptured catches any render / lifecycle / watcher error thrown by the
// subtree (the whole <router-view>), records it in Sentry as its own tagged
// event, and swaps the broken subtree for a screen the customer can act on.
//
// Why it exists: on 1-3 Sep a TDZ bug in NewRequest threw during render and
// customers got the header plus a blank white body on /request. Sentry saw the
// Vue error, but nothing said "the customer is looking at nothing" — and the
// customer had no reference to quote. Both are fixed here.
import { ref, computed, watch, onErrorCaptured } from 'vue';
import { useRoute } from 'vue-router';
import * as Sentry from '@sentry/vue';

const route = useRoute();

const crashed  = ref(false);
const eventId  = ref<string | null>(null);
// Bumped on every reset so the slot subtree is fully re-created rather than
// re-using the instances that were live when the error was thrown.
const slotKey  = ref(0);

const surface = computed(() => (route.name as string) || route.path || 'unknown');

onErrorCaptured((err, _instance, info) => {
  // A component that throws in setup usually throws again while Vue finishes
  // tearing it down (its refs are never assigned), so one crash arrives here
  // several times. Report the first, swallow the rest: one customer-visible
  // crash should be one Sentry event.
  if (crashed.value) return false;

  // Its own Sentry event, tagged so `app_crash:true` can carry an alert rule of
  // its own — separate from the ordinary handled errors in the issue stream.
  try {
    eventId.value = Sentry.captureException(err, {
      tags: {
        app_crash: 'true',
        crash_surface: surface.value,
      },
      level: 'fatal',
      extra: {
        lifecycle_hook: info,
        path: route.fullPath,
      },
    });
  } catch {
    // Reporting must never be the reason the crash screen fails to show.
  }

  crashed.value = true;
  console.error('[portal] render crash captured by ErrorBoundary', err);

  // Stop propagation: without this the error also reaches app.config.errorHandler
  // and would be reported a second time, untagged.
  return false;
});

function reset() {
  crashed.value = false;
  eventId.value = null;
  slotKey.value += 1;
}

// Navigating away recovers on its own — the crashed route is gone, so there is
// nothing left to show the crash screen for.
watch(() => route.fullPath, () => {
  if (crashed.value) reset();
});

function reload() {
  window.location.reload();
}
</script>

<template>
  <div v-if="crashed" class="eb-screen">
    <div class="eb-card">
      <div class="eb-icon">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </div>

      <h1 class="eb-title">This page hit a problem</h1>
      <p class="eb-body">
        It has been reported to our team automatically. Reloading usually clears it.
      </p>

      <div class="eb-actions">
        <button class="eb-btn eb-btn--primary" @click="reload">Reload page</button>
        <router-link class="eb-btn eb-btn--ghost" to="/transactions/orders">Go to my orders</router-link>
      </div>

      <p v-if="eventId" class="eb-ref">
        Reference <span class="eb-ref-id">{{ eventId }}</span>
      </p>
    </div>
  </div>

  <template v-else>
    <slot :key="`rv-${slotKey}`" />
  </template>
</template>

<style scoped>
.eb-screen {
  flex: 1;
  width: 100%;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  background: #f0f6ff;
  font-family: 'DM Sans', 'Nunito', sans-serif;
}

.eb-card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border: 1.5px solid #ccdeff;
  border-radius: 12px;
  padding: 32px 28px;
  text-align: center;
  box-shadow: 0 12px 40px rgba(0, 61, 160, 0.08);
}

.eb-icon {
  width: 44px;
  height: 44px;
  margin: 0 auto 16px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffe4e6;
  color: #eb1f28;
}

.eb-title {
  margin: 0 0 8px;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f1729;
}

.eb-body {
  margin: 0 0 22px;
  font-size: 0.85rem;
  font-weight: 400;
  line-height: 1.5;
  color: #4a5568;
}

.eb-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}

.eb-btn {
  height: 40px;
  padding: 0 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.eb-btn--primary {
  background: #0061eb;
  color: #fff;
  border: 1.5px solid #0061eb;
}
.eb-btn--primary:hover { background: #0050c7; border-color: #0050c7; }

.eb-btn--ghost {
  background: #fff;
  color: #0061eb;
  border: 1.5px solid #ccdeff;
}
.eb-btn--ghost:hover { border-color: #0061eb; }

.eb-ref {
  margin: 20px 0 0;
  font-size: 0.7rem;
  font-weight: 400;
  color: #8a95a6;
}

.eb-ref-id {
  font-weight: 600;
  letter-spacing: 0.02em;
  word-break: break-all;
}
</style>
